import type { CaseNote, CaseNoteType, NoteOptions, NoteProgramOption, NoteServiceUnit } from '@/db/types/case_note';
import type { CaseNoteDraftInput } from '@/db/services/case_note_service';

/** What the note form edits. Ids are kept as numbers or null; text as strings. */
export interface CaseNoteFormState {
  type: CaseNoteType;
  programId: number | null;
  siteId: number | null;
  /** YYYY-MM-DD */
  interactionDate: string;
  interactionType: string | null;
  /** Free text, or the name of a preset subject. */
  subject: string;
  noteSubjectId: number | null;
  checkIn: string | null;
  detail: string;
  /** Keyed by service definition id. */
  units: Record<number, { count: string; fundingSourceId: number | null }>;
}

export type CaseNoteFormErrors = Partial<Record<'program' | 'site' | 'interactionDate' | 'interactionType' | 'subject' | 'detail' | 'units', string>>;

/** Today in the device's time zone, as YYYY-MM-DD. */
export function todayLocal(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function initialFormState(options: NoteOptions, note?: CaseNote | null): CaseNoteFormState {
  if (note) {
    return {
      type: note.type,
      programId: note.program?.id ?? null,
      siteId: note.site?.id ?? null,
      interactionDate: note.interaction_date,
      interactionType: note.interaction_type,
      subject: note.note_subject?.name ?? note.subject ?? '',
      noteSubjectId: note.note_subject?.id ?? null,
      checkIn: note.check_in,
      detail: note.detail,
      units: Object.fromEntries(note.service_units.map(unit => [
        unit.service_definition_id,
        { count: unit.unit_count, fundingSourceId: unit.funding_source?.id ?? null },
      ])),
    };
  }

  const canWriteCaseNote = options.note_types.includes('CaseNote') && options.programs.length > 0;
  const type: CaseNoteType = canWriteCaseNote || !options.note_types.includes('BasicNote') ? 'CaseNote' : 'BasicNote';
  const onlyProgram = options.programs.length === 1 ? options.programs[0].id : null;
  return {
    type,
    programId: onlyProgram,
    siteId: options.default_site_id,
    interactionDate: todayLocal(),
    interactionType: null,
    subject: '',
    noteSubjectId: null,
    checkIn: null,
    detail: '',
    units: {},
  };
}

export function selectedProgram(state: CaseNoteFormState, options: NoteOptions): NoteProgramOption | null {
  return options.programs.find(program => program.id === state.programId) ?? null;
}

export function validateCaseNoteForm(state: CaseNoteFormState, options: NoteOptions): CaseNoteFormErrors {
  const errors: CaseNoteFormErrors = {};
  if (!state.interactionDate) errors.interactionDate = 'Enter the date of the interaction.';
  if (!state.detail.trim()) errors.detail = 'Write the note.';

  if (state.type === 'BasicNote') {
    if (!state.interactionType) errors.interactionType = 'Choose a source.';
    return errors;
  }

  const program = selectedProgram(state, options);
  if (!program) {
    errors.program = 'Choose a program.';
    return errors;
  }
  if (options.requires_site && !state.siteId) errors.site = 'Choose a site.';
  if (program.uses_coding && !state.interactionType) errors.interactionType = 'Choose how you met with the client.';
  if (program.subject.mode !== 'none' && !state.noteSubjectId && !state.subject.trim()) errors.subject = 'Choose a subject.';
  if (program.subject.mode === 'preset' && !state.noteSubjectId && state.subject.trim()) errors.subject = 'Choose a subject from the list.';

  for (const definition of program.service_definitions) {
    const raw = state.units[definition.id]?.count?.trim();
    if (!raw) continue;
    const count = Number(raw);
    if (!Number.isFinite(count) || count < 0) {
      errors.units = `Enter a number of ${definition.unit_name}s for ${definition.name}.`;
    } else if (!definition.accepts_partial_units && !Number.isInteger(count)) {
      errors.units = `${definition.name} is logged in whole ${definition.unit_name}s.`;
    }
  }
  return errors;
}

/** Resolve the subject input against the program's preset list. */
function resolveSubject(state: CaseNoteFormState, program: NoteProgramOption | null) {
  const typed = state.subject.trim();
  const preset = program?.subject.options.find(option =>
    option.id === state.noteSubjectId || option.name.toLowerCase() === typed.toLowerCase()
  );
  if (preset) return { note_subject: { id: preset.id, name: preset.name }, subject: preset.name };
  return { note_subject: null, subject: typed || null };
}

export function formToDraftInput(
  state: CaseNoteFormState,
  options: NoteOptions,
  context: { clientId: number; organizationId: number; author: CaseNote['author'] },
): CaseNoteDraftInput {
  const base = {
    type: state.type,
    client_id: context.clientId,
    organization_id: context.organizationId,
    interaction_date: state.interactionDate,
    interaction_type: state.interactionType,
    detail: state.detail.trim(),
    author: context.author,
  };

  if (state.type === 'BasicNote') {
    return {
      ...base,
      subject: state.subject.trim() || null,
      note_subject: null,
      check_in: null,
      program: null,
      site: null,
      service_units: [],
    };
  }

  const program = selectedProgram(state, options);
  const site = options.sites.find(candidate => candidate.id === state.siteId) ?? null;
  const serviceUnits: NoteServiceUnit[] = (program?.service_definitions ?? []).flatMap(definition => {
    const entry = state.units[definition.id];
    const count = Number(entry?.count);
    if (!entry || !Number.isFinite(count) || count <= 0) return [];
    const fundingSource = definition.funding_sources.find(source => source.id === entry.fundingSourceId) ?? null;
    return [{
      service_definition_id: definition.id,
      name: definition.name,
      unit_count: String(count),
      unit_name: definition.unit_name,
      funding_source: fundingSource,
    }];
  });

  return {
    ...base,
    interaction_type: program?.uses_coding ? state.interactionType : null,
    ...(program?.subject.mode === 'none' ? { note_subject: null, subject: null } : resolveSubject(state, program)),
    check_in: program?.uses_check_in ? state.checkIn : null,
    program: program ? { id: program.id, name: program.name } : null,
    site: site ? { id: site.id, name: site.name } : null,
    service_units: serviceUnits,
  };
}
